<script setup lang="ts">
import moment, { type Moment } from "moment";
import { DatePicker as DatePickerExternal } from "v-calendar";
import { computed, inject, nextTick, ref, watch } from "vue";

import Button from "~/components/dumb/Button.vue";
import TimePicker from "~/components/dumb/TimePicker.vue";
import { RECURRENCE_EDITOR } from "~/constants/injection";
import { BACKUP_FONTS } from "~/constants/style";
import { ArrowRightIcon, RecommendWithAiIcon, XIcon } from "~/icons";
import { ButtonStyle, IconSize, RecurrenceKind, UserRole } from "~/shared/enums";
import type { DatePickerDate, Recurrence, SimpleDateRange, Task } from "~/shared/types";
import { useDataStore, usePageStore, useUserStore } from "~/stores";
import { at8am, formatDate } from "~/utils/date";
import {
  getNextRecursNextAt,
  getRecurrenceDatesInRange,
  getRecurrenceSummary,
  getRelativeTimeForDatesDate,
  isBrowserLocale24h,
} from "~/utils/time";

const RecurrenceEditor = inject(RECURRENCE_EDITOR);

const props = defineProps<{
  // date picker
  value: DatePickerDate;
  showRecommendationButton?: boolean;
  maxStartDate?: Date;
  preventUnset?: boolean;
  // recurrence reminder dropdown
  isReminder?: boolean;
  isRecurrence?: boolean;
  remindAt: string | null;
  recurrence: Recurrence | null;
  recursNextAt: string | null;
  tasks?: Task[];
  // common
  onKeydown?: (event: KeyboardEvent) => void;
}>();

const emit = defineEmits<{
  // date picker
  select: [selectedDate: string | null];
  selectRange: [selectedRange: SimpleDateRange];
  recommend: [];
}>();

const dataStore = useDataStore();
const pageStore = usePageStore();
const userStore = useUserStore();

const isDateRange = (value: DatePickerDate): value is SimpleDateRange => !!value && typeof value === "object";
const toDateMaybe = (str: string | null | undefined) => (str ? moment(str).toDate() : null);
const areSame = (a: Date | null, b: Date | null) => (!a && !b) || (!!a && !!b && moment(a).isSame(b));

const datePicker = ref<InstanceType<typeof DatePickerExternal> | null>(null);
const focusDiv = ref<HTMLDivElement | null>(null);

// v-date-picker only works with v-model, so we have to save it like this
const startSelected = ref(false);
const showSelection = ref(false);
const singleSelectedDate = ref(false);
const selectedDate = ref(toDateMaybe(isDateRange(props.value) ? props.value.start : props.value));
const hasStart = computed(() => !!(typeof props.value === "object" && props.value?.start));
const hasEnd = computed(() => !!(typeof props.value === "object" && props.value?.end));

const currentRemindAt = ref<string | null>(props.remindAt);
const currentRecurrence = ref<Recurrence | null>(props.recurrence);
const currentRecursNextAt = ref<string | null>(props.recursNextAt);

const showStartingDate = computed(() => {
  if (
    currentRecurrence.value?.kind === RecurrenceKind.MONTHLY ||
    currentRecurrence.value?.kind === RecurrenceKind.YEARLY
  ) {
    return true;
  }

  return (
    (currentRecurrence.value?.kind === RecurrenceKind.DAILY ||
      currentRecurrence.value?.kind === RecurrenceKind.WEEKLY) &&
    currentRecurrence.value.interval > 1
  );
});

const hasRecurrence = computed(() => !!currentRecurrence.value && !!currentRecursNextAt.value);

const currentDateValue = computed(() => {
  if (props.isReminder && currentRemindAt.value) {
    return currentRemindAt.value;
  }
  if (typeof props.value === "string") {
    return props.value;
  }
  return null;
});

const setSelectedDate = (newValue: DatePickerDate) => {
  const newDate = toDateMaybe(isDateRange(newValue) ? (startSelected.value ? newValue.start : newValue.end) : newValue);
  if (areSame(newDate, selectedDate.value)) {
    return;
  }
  selectedDate.value = newDate;
};

const updateReminder = (remindAt: Moment | null) => {
  const newRemindAt = remindAt ? remindAt.toISOString() : null;
  currentRemindAt.value = newRemindAt;
  if (props.tasks) {
    dataStore.updateTasks(
      props.tasks.map((e) => ({
        duid: e.duid,
        remindAt: newRemindAt,
      }))
    );
  }
};
const updateRecurrence = (recurrence: Recurrence | null) => {
  const recursNextAt = recurrence
    ? currentRecursNextAt.value || at8am(getNextRecursNextAt(recurrence, moment())).toISOString()
    : null;
  currentRecurrence.value = recurrence;
  currentRecursNextAt.value = recursNextAt;
  if (props.tasks) {
    dataStore.updateTasks(
      props.tasks.map((e) => ({
        duid: e.duid,
        recurrence,
        recursNextAt,
      }))
    );
  }
};
const updateRecursNextAt = (recursNextAt: string | null) => {
  currentRecursNextAt.value = recursNextAt;
  if (props.tasks) {
    dataStore.updateTasks(
      props.tasks.map((e) => ({
        duid: e.duid,
        recursNextAt,
      }))
    );
  }
};
watch(() => props.value, setSelectedDate);
watch(startSelected, () => setSelectedDate(props.value));
watch(
  () => selectedDate.value,
  (newDate) => {
    if ((props.isReminder || props.isRecurrence) && newDate && moment(newDate).isBefore(moment(), "day")) {
      setSelectedDate(props.isReminder ? currentRemindAt.value : currentRecursNextAt.value);
      return;
    }

    const result = newDate ? formatDate(newDate) : null;
    if (props.isReminder) {
      const reminderMoment =
        newDate && moment(newDate).isSame(moment(), "day")
          ? moment()
              .add(15 - (moment().minute() % 15) || 15, "minutes")
              .startOf("minute")
          : newDate
            ? moment(newDate)
            : null;
      updateReminder(reminderMoment);
      return;
    }
    if (props.isRecurrence) {
      updateRecursNextAt(result);
      return;
    }

    if (!isDateRange(props.value)) {
      const oldValue = toDateMaybe(props.value);
      if (areSame(newDate, oldValue)) {
        return;
      }

      emit("select", result);
      return;
    }

    const oldValue = toDateMaybe(startSelected.value ? props.value.start : props.value.end);
    if (areSame(newDate, oldValue)) {
      return;
    }

    const conflict = startSelected.value
      ? props.value.end && moment(newDate).isAfter(props.value.end)
      : props.value.start && moment(newDate).isBefore(props.value.start);
    if (conflict && !showSelection.value) {
      emit("selectRange", {
        ...props.value,
        [startSelected.value ? "end" : "start"]: result,
      });
      return;
    }

    emit("selectRange", {
      ...props.value,
      [startSelected.value ? "start" : "end"]: result,
      ...(conflict ? { [startSelected.value ? "end" : "start"]: null } : {}),
    });

    if (startSelected.value) {
      startSelected.value = false;
    } else if (showSelection.value) {
      showSelection.value = false;
    }
  }
);

const valueButtonStyle =
  "flex h-8 flex-1 items-center justify-center text-left overflow-hidden gap-2 rounded border bg-lt hover:bg-md/80 pl-2 pr-1 py-1 text-sm text-md focus-ring-none";

const selectRangeInput = async (newStartSelected: boolean) => {
  startSelected.value = newStartSelected;
  showSelection.value = true;
};

const selectSingleDateInput = () => {
  singleSelectedDate.value = true;
};

const scrollToCurrentDate = async () => {
  await nextTick();
  if (!selectedDate.value) {
    return;
  }
  datePicker.value?.moveToValue(selectedDate.value);
};

const onClickRecommendationButton = () => {
  emit("recommend");
};

const jumpToToday = () => {
  const today = moment().startOf("day");
  datePicker.value?.move({ month: today.month() + 1, year: today.year() });
};

const currentMonthStart = ref(moment().startOf("month"));

const onMove = () => {
  const focusedPage = datePicker.value.calendarRef.firstPage;
  currentMonthStart.value = moment()
    .year(focusedPage.year)
    .month(focusedPage.month - 1)
    .startOf("month");
};

const onDayClick = (day: { inMonth: boolean; date: string }) => {
  if (!(day && !day.inMonth && day.date)) {
    return;
  }
  nextTick(() => datePicker.value?.moveToValue(new Date(day.date)));
};

const recurrenceDateAttributes = computed(() => {
  if (!currentRecurrence.value || !currentRecursNextAt.value) {
    return {};
  }
  const now = moment().startOf("day");
  const currStart = moment(currentMonthStart.value).subtract(1, "month").startOf("month");
  const recurrenceStart = moment(currentRecursNextAt.value).startOf("day");
  const startDate = moment.max(now, currStart, recurrenceStart);
  const endDate = moment(startDate).add(3, "month").startOf("month");
  const dates = getRecurrenceDatesInRange(
    currentRecurrence.value,
    moment(currentRecursNextAt.value),
    startDate,
    endDate
  ).map((e) => e.toDate());
  return { key: "recurrence", dot: "green", dates };
});

const pastDateAttributes = computed(() => {
  if (!props.isReminder && !props.isRecurrence) {
    return null;
  }

  return {
    key: "past-dates",
    dates: {
      end: moment().subtract(1, "day").endOf("day").toDate(),
    },
    content: {
      class: "dart-past-date",
    },
  };
});

const highlights = computed(() => {
  const res: unknown[] = [];
  if (pastDateAttributes.value) {
    res.push(pastDateAttributes.value);
  }
  if (!props.isReminder && !props.isRecurrence && isDateRange(props.value) && (props.value.start || props.value.end)) {
    res.push({
      key: "range",
      highlight: "blue",
      dates: { start: moment(props.value.start).toDate(), end: moment(props.value.end).toDate() },
    });
  }
  if (props.isReminder && currentRemindAt.value) {
    res.push({ key: "reminder", dot: "yellow", dates: moment(currentRemindAt.value).toDate() });
  }
  if (props.isRecurrence && currentRecurrence.value && currentRecursNextAt.value) {
    res.push(recurrenceDateAttributes.value);
  }
  res.push({ key: "today", highlight: { color: "indigo", fillMode: "outline" }, dates: new Date() });
  return res;
});

const onKeydown = (event: KeyboardEvent) => {
  props.onKeydown?.(event);
};

const resetSelectionStates = () => {
  startSelected.value = false;
  showSelection.value = false;
  singleSelectedDate.value = false;
};

defineExpose({
  datePicker,
  startSelected,
  showSelection,
  singleSelectedDate,
  selectedDate,
  setSelectedDate,
  scrollToCurrentDate,
  jumpToToday,
  onMove,
  onDayClick,
  resetSelectionStates,
});
</script>

<template>
  <div :class="pageStore.isMobile && 'flex flex-col px-6 pt-2 pb-4'" @click.stop @keydown.stop="onKeydown">
    <div v-if="!pageStore.isMobile" ref="focusDiv" tabindex="0" class="focus:outline-none" />
    <!-- Top bar: when recurrence, show summary + clear; otherwise date/reminder inputs -->
    <div v-if="!isRecurrence" class="flex items-center gap-2 px-3 pt-3 select-none">
      <button
        v-if="!isDateRange(value) || isReminder"
        type="button"
        :class="[valueButtonStyle, singleSelectedDate ? 'border-primary-base' : 'border-md']"
        @click="selectSingleDateInput"
        @dblclick="scrollToCurrentDate">
        <span v-if="currentDateValue" class="flex-1 truncate">
          {{ getRelativeTimeForDatesDate(currentDateValue) }}
        </span>
        <span v-else class="flex-1 text-vlt">Select</span>
        <div
          v-if="!preventUnset && (value || (isReminder && currentRemindAt))"
          class="cursor-pointer rounded-sm p-0.5 hover:bg-hvy/80"
          @click.stop="isReminder ? updateReminder(null) : emit('select', null)"
          @keydown.enter="isReminder ? updateReminder(null) : emit('select', null)">
          <XIcon class="icon-xs text-lt" />
        </div>
      </button>
      <template v-else>
        <button
          type="button"
          :class="[valueButtonStyle, startSelected && showSelection ? 'border-primary-base' : 'border-md']"
          @click="selectRangeInput(true)"
          @dblclick="scrollToCurrentDate">
          <span v-if="value.start" class="flex-1 truncate">
            {{ getRelativeTimeForDatesDate(value.start) }}
          </span>
          <span v-else class="flex-1 text-vlt">Start</span>
          <div
            v-if="!preventUnset && hasStart"
            class="cursor-pointer rounded-sm p-0.5 hover:bg-hvy/80"
            @click="emit('selectRange', { start: null, end: value.end })"
            @keydown.enter="emit('selectRange', { start: null, end: value.end })">
            <XIcon class="icon-xs text-lt" />
          </div>
        </button>
        <ArrowRightIcon class="size-3 shrink-0 text-lt" />
        <button
          type="button"
          :class="[valueButtonStyle, !startSelected && showSelection ? 'border-primary-base' : 'border-md']"
          @click="selectRangeInput(false)"
          @dblclick="scrollToCurrentDate">
          <span v-if="value.end" class="flex-1 truncate">
            {{ getRelativeTimeForDatesDate(value.end) }}
          </span>
          <span v-else class="flex-1 text-vlt">End</span>
          <div
            v-if="!preventUnset && hasEnd"
            class="cursor-pointer rounded-sm p-0.5 hover:bg-hvy/80"
            @click="emit('selectRange', { start: value.start, end: null })"
            @keydown.enter="emit('selectRange', { start: value.start, end: null })">
            <XIcon class="icon-xs text-lt" />
          </div>
        </button>
      </template>

      <!-- Time picker for reminder -->
      <TimePicker
        v-if="isReminder && currentRemindAt"
        :value="currentRemindAt"
        :interval-min="15"
        @select="(selectedDate: string) => updateReminder(selectedDate ? moment(selectedDate) : null)" />
    </div>
    <div
      v-else-if="hasRecurrence"
      class="mx-3 mt-3 select-none"
      :class="[valueButtonStyle, 'cursor-default !justify-between border-md pl-3']">
      <span class="flex-1 truncate">
        {{ `Repeats ${getRecurrenceSummary(currentRecurrence!, moment(currentRecursNextAt!))}` }}
      </span>
      <div
        class="cursor-pointer rounded-sm p-0.5 hover:bg-hvy/80"
        @click.stop="updateRecurrence(null)"
        @keydown.enter="updateRecurrence(null)">
        <XIcon class="icon-xs text-lt" />
      </div>
    </div>

    <div class="flex flex-1 flex-col">
      <!-- Recurrence editor above calendar; outside calendar's relative area -->
      <div v-if="isRecurrence" class="p-3">
        <RecurrenceEditor
          v-if="currentRecurrence !== undefined && currentRecursNextAt !== undefined"
          :recurrence="currentRecurrence"
          :recurs-next-at="currentRecursNextAt"
          expanded
          small
          is-contrast
          @change-recurrence="updateRecurrence"
          @change-recurs-next-at="updateRecursNextAt" />
      </div>

      <div class="relative">
        <DatePickerExternal
          v-if="!isRecurrence || hasRecurrence"
          ref="datePicker"
          v-model="selectedDate"
          mode="date"
          color="indigo"
          title-position="left"
          expanded
          :class="{
            'dart-non-interactive-days': isRecurrence && !showStartingDate,
            'dart-recurrence-reminder-mode': isRecurrence || isReminder,
          }"
          :attributes="highlights"
          :is-dark="pageStore.theme === 'dark'"
          :is24hr="isBrowserLocale24h()"
          :first-day-of-week="(userStore.firstDayOfWeek ?? 1) + 1"
          :max-date="startSelected ? maxStartDate : undefined"
          :style="{ fontFamily: `Inter var, ${BACKUP_FONTS}` }"
          @dayclick="onDayClick"
          @transition-end="onMove" />
        <button
          v-if="!isRecurrence || hasRecurrence"
          type="button"
          class="absolute top-[15px] rounded-sm px-1 text-sm text-md focus-ring-none hover:bg-md"
          :class="pageStore.isMobile ? 'right-[80px]' : 'right-[70px]'"
          @click="jumpToToday">
          Today
        </button>
      </div>
    </div>

    <div
      v-if="
        showRecommendationButton &&
        !isRecurrence &&
        !isReminder &&
        pageStore.isOnline &&
        userStore.isRoleGte(UserRole.MEMBER)
      "
      class="px-3 pb-2">
      <Button
        class="!justify-start border !border-md py-1.5"
        :btn-style="ButtonStyle.SECONDARY"
        text="Fill out with AI"
        :icon="RecommendWithAiIcon"
        :icon-size="IconSize.S"
        block
        borderless
        :icon-args="{ class: 'text-recommendation-base' }"
        is-contrast
        @click="onClickRecommendationButton" />
    </div>
  </div>
</template>
